import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "./TokenContext";
import mio from "../img/mio.png";
import GATB from "../img/gatb.webp";
import hobby from "../img/tekishoku.webp";
import consultant from "../img/consultant.png";
import StudySphereIcon from "../img/StudySphere.ico";
import portalSound from "../voice/Portal.wav";
import "../css/portal.css";

function Portal() {
  const {
    userName,
    userRole,
    locationNames,
  } = useToken();

  const audioRef = useRef(null);
  const navigate = useNavigate();
  const [hasGATB, setHasGATB] = useState(false);
  const [hasCalling, setHasCalling] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isLoadingStudySphere, setIsLoadingStudySphere] = useState(false);

  // ✅ 共通チェック関数
  const checkTestExists = async (type) => {
    const loginCode = localStorage.getItem("loggedInToken");
    if (!loginCode) return false;

    try {
      const response = await fetch("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/checkAttended", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginCode, type }),
      });
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error(`${type} チェック中にエラーが発生しました:`, error);
      return false;
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(portalSound);
    audioRef.current = audio;
    audio.play().catch((error) => {
      console.error("音声再生に失敗しました:", error);
    });

    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  // GATBとcallingの受講状況をチェック
  useEffect(() => {
    const checkBothTests = async () => {
      setIsChecking(true);
      const gatbExists = await checkTestExists("GATB");
      const callingExists = await checkTestExists("calling");
      setHasGATB(gatbExists);
      setHasCalling(callingExists);
      setIsChecking(false);
    };

    checkBothTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    if (window.confirm("ログアウトしてもよろしいですか？")) {
      localStorage.clear();
      navigate("/");
    }
  };

  // ✅ GATBクリック時
  const handleGATBClick = async () => {
    const exists = await checkTestExists("GATB");
    if (exists) {
      const confirmRes = window.confirm("本検査は既に受講済みです。結果が上書きされますが、再受講しますか？");
      if (!confirmRes) return;
    }
    navigate("/GATBstart");
  };

  // ✅ callingクリック時
  const handleCallingClick = async () => {
    const exists = await checkTestExists("calling");
    if (exists) {
      const confirmRes = window.confirm("本検査は既に受講済みです。結果が上書きされますが、再受講しますか？");
      if (!confirmRes) return;
    }
    navigate("/questionnaire");
  };

  // ✅ コンサルタントページへ遷移
  const handleConsultantClick = () => {
    const userName = localStorage.getItem("userName");
    
    // 拠点名を取得（locationNamesを優先、なければcompanyName_findjob）
    const locationNamesStr = localStorage.getItem("locationNames");
    let affiliation = null;
    if (locationNamesStr) {
      try {
        const locationNames = JSON.parse(locationNamesStr);
        if (Array.isArray(locationNames) && locationNames.length > 0) {
          affiliation = locationNames.join('、'); // 複数拠点の場合は「、」で結合
        } else if (typeof locationNames === 'string') {
          affiliation = locationNames;
        }
      } catch (e) {
        // JSON解析に失敗した場合は文字列として扱う
        affiliation = locationNamesStr;
      }
    }
    
    // 拠点名が取得できない場合は企業名をフォールバック
    if (!affiliation) {
      affiliation = localStorage.getItem("companyName_findjob");
    }
    
    navigate("/consultant", {
      state: {
        userName,
        affiliation,
      },
    });
  };

  // ✅ StudySphereバックエンドAPI URLを取得
  const getStudySphereApiUrl = () => {
    // 環境変数から取得を試みる
    const envUrl = process.env.REACT_APP_STUDYSPHERE_API_URL;
    if (envUrl) {
      return envUrl.trim();
    }

    // ホスト名から環境を判定
    const hostname = window.location.hostname || '';
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname) || hostname.endsWith('.local');
    
    // 本番環境の判定
    const isProductionByEnv = process.env.NODE_ENV === 'production';
    const isProductionByHost = hostname.includes('studysphere') || hostname.includes('ayatori-inc.co.jp') || hostname.includes('myou-kou.com');
    const isProduction = isProductionByEnv || isProductionByHost;

    // ローカルホストの場合は開発環境のURLを使用
    if (isLocalhost) {
      return 'http://localhost:5050';
    }

    // 本番環境の場合は本番API URLを使用
    return 'https://backend.studysphere.ayatori-inc.co.jp';
  };

  // ✅ StudySphereフロントエンドURLを取得
  const getStudySphereFrontendUrl = () => {
    const hostname = window.location.hostname || '';
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname) || hostname.endsWith('.local');
    
    if (isLocalhost) {
      return 'http://localhost:3000';
    }

    // 本番環境のURL
    return 'https://studysphere.ayatori-inc.co.jp';
  };

  // ✅ ロールに応じたダッシュボードパスを決定
  const getDashboardPath = (role) => {
    switch (role) {
      case 1: // 利用者
        return '/student/dashboard';
      case 4: // 指導員
      case 5: // 管理者
        return '/instructor/dashboard';
      case 9: // アドミン
      case 10: // マスターユーザー
        return '/admin/dashboard';
      default:
        return '/student/dashboard';
    }
  };

  // ✅ StudySphereダッシュボードへ遷移（SSOログイン）
  const handleStudySphereClick = async () => {
    setIsLoadingStudySphere(true);
    
    try {
      const loginCode = localStorage.getItem("loggedInToken");
      if (!loginCode) {
        alert("ログイン情報が見つかりません。再度ログインしてください。");
        setIsLoadingStudySphere(false);
        return;
      }

      const frontendUrl = getStudySphereFrontendUrl();
      
      // Step 1: findjob側のLambda関数を呼び出してSSOチケットを生成
      const lambdaApiUrl = "https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api";
      
      const ticketResponse = await fetch(`${lambdaApiUrl}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "generate-sso-ticket",
          loginCode: loginCode,
        }),
      });

      if (!ticketResponse.ok) {
        const errorData = await ticketResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "SSOチケットの生成に失敗しました");
      }

      const ticketData = await ticketResponse.json();
      
      if (!ticketData.success || !ticketData.data?.ticket) {
        throw new Error(ticketData.message || "SSOチケットの生成に失敗しました");
      }

      const ticket = ticketData.data.ticket;
      console.log("SSOチケット生成成功:", ticket);

      // Step 2: 生成されたチケットを使ってStudySphereのSSOログインページにリダイレクト
      const ssoLoginUrl = `${frontendUrl}/sso-login?ticket=${encodeURIComponent(ticket)}`;
      
      // 新しいタブで開く
      window.open(ssoLoginUrl, '_blank');
      
    } catch (error) {
      console.error("StudySphere遷移エラー:", error);
      alert(`StudySphereへの遷移に失敗しました: ${error.message}`);
    } finally {
      setIsLoadingStudySphere(false);
    }
  };

  return (
    <div className="portal-container">
      <h1 className="portal-heading">
        ようこそ、{userName || "利用者"}さん<br />
        {locationNames && locationNames.length > 0 ? (
          <span>（所属拠点：{Array.isArray(locationNames) ? locationNames.join('、') : locationNames}）</span>
        ) : (
          <span>（所属拠点：未設定）</span>
        )}
      </h1>

      <div className="portal-menu">
        <div className="menu-area">
          <ul>
            <li>
              <div onClick={handleGATBClick}>
                <img src={GATB} alt="GATB" />
                <p>厚生労働省編一般職業適性検査（GATB）</p>
              </div>
            </li>
            <li>
              <div onClick={handleCallingClick}>
                <img src={hobby} alt="hobby" />
                <p>興味分野適職診断</p>
              </div>
            </li>
            {/* ✅ GATBとcallingの両方受講済みの場合、コンサルタントページへのリンクを表示 */}
            {!isChecking && hasGATB && hasCalling && (
              <li>
                <div onClick={handleConsultantClick}>
                  <img src={consultant} alt="consultant" />
                  <p>適職コンサルティング</p>
                </div>
              </li>
            )}
          </ul>
        </div>

        <div className="mio">
          <div className="speech-bubble">
            今日はどのページにご案内しましょうか
          </div>
          <img src={mio} alt="綾鳥みお" className="mio-image" />
        </div>
      </div>

      {/* ✅ ロールが5以上（管理者 or アドミン）の場合のみ表示 */}
      {userRole >= 5 && (
        <div>
          <button onClick={() => navigate("/admin")} className="portal-button logout">
            管理者用ページへ
          </button>
        </div>
      )}
      <div>
        <button onClick={handleLogout} className="portal-button logout">
          ログアウト
        </button>
      </div>

      {/* ✅ StudySphere.icoを左下に表示（SSOログインボタン） */}
      <div className="studysphere-link-area">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (!isLoadingStudySphere) {
              handleStudySphereClick();
            }
          }}
          className={`studysphere-link ${isLoadingStudySphere ? 'loading' : ''}`}
          title={isLoadingStudySphere ? "ログイン処理中..." : "StudySphereにSSOログイン"}
        >
          {isLoadingStudySphere ? (
            <div className="studysphere-loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <img 
              src={StudySphereIcon} 
              alt="StudySphere" 
              className="studysphere-icon"
            />
          )}
        </a>
      </div>
    </div>
  );
}

export default Portal;
